import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useRazorpay } from "react-razorpay";
import { API, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { 
  Check, 
  Loader2, 
  CreditCard,
  Sparkles,
  Shield,
  Zap,
  ArrowLeft,
  Home
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_ff018244-580d-47eb-b754-fa20aec11c8d/artifacts/2zc3j2vv_jini%20studio%20logo%20.png";

const PLANS = [
  { 
    id: "starter",
    name: "Starter", 
    price: 999, 
    period: "/month",
    features: [
      "50 SKU Credits",
      "200 AI Images",
      "50 Videos",
      "All Categories",
      "Email Support",
      "Standard Quality"
    ],
    popular: false,
    color: "bg-gray-100"
  },
  { 
    id: "pro",
    name: "Pro", 
    price: 2499, 
    period: "/month",
    features: [
      "200 SKU Credits",
      "800 AI Images",
      "200 Videos",
      "All Categories",
      "Priority Support",
      "Team Access (5 members)",
      "High Quality Output"
    ],
    popular: true,
    color: "bg-indigo-600"
  },
  { 
    id: "studio",
    name: "Studio", 
    price: 4999, 
    period: "/month",
    features: [
      "500 SKU Credits",
      "2000 AI Images",
      "500 Videos",
      "All Categories",
      "Dedicated Support",
      "Team Access (Unlimited)",
      "API Access",
      "Premium Quality Output"
    ],
    popular: false,
    color: "bg-gray-100"
  }
];

export default function PricingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { error, isLoading, Razorpay } = useRazorpay();
  const [loading, setLoading] = useState(null);

  const handleLogin = () => {
    const currentUrl = window.location.href;
    const redirectUrl = currentUrl.replace(/\/$/, '');
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handlePayment = async (planId) => {
    if (!user) {
      toast.info("Please login to subscribe");
      handleLogin();
      return;
    }

    if (isLoading || !Razorpay) {
      toast.error("Payment system is loading, please wait...");
      return;
    }

    setLoading(planId);

    try {
      // Create order on backend
      const orderResponse = await axios.post(`${API}/payments/create-order`, {
        plan_id: planId,
        amount: PLANS.find(p => p.id === planId)?.price * 100 || 0,
        currency: "INR"
      }, { withCredentials: true });

      const { order_id, amount, currency, key_id, plan_name, user_name, user_email } = orderResponse.data;

      // Open Razorpay checkout
      const options = {
        key: key_id,
        amount: amount,
        currency: currency,
        name: "Jini Studio AI",
        description: `${plan_name} Plan Subscription`,
        order_id: order_id,
        handler: async (response) => {
          try {
            // Verify payment on backend
            const verifyResponse = await axios.post(`${API}/payments/verify`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              plan_id: planId
            }, { withCredentials: true });

            toast.success(verifyResponse.data.message);
            navigate("/dashboard");
          } catch (error) {
            toast.error("Payment verification failed. Please contact support.");
          }
        },
        prefill: {
          name: user_name,
          email: user_email,
          contact: ""
        },
        notes: {
          plan_id: planId
        },
        theme: {
          color: "#4338ca"
        },
        modal: {
          ondismiss: () => {
            setLoading(null);
          }
        }
      };

      const rzp = new Razorpay(options);
      rzp.on("payment.failed", (response) => {
        toast.error(`Payment failed: ${response.error.description}`);
        setLoading(null);
      });
      rzp.open();

    } catch (error) {
      const message = error.response?.data?.detail || "Failed to create payment order";
      toast.error(message);
      setLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Header */}
      <header className="bg-white border-b border-neutral-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="Jini Studio" className="h-10 w-auto" />
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-neutral-600 hover:text-neutral-900 text-sm">
              <ArrowLeft className="w-4 h-4 inline mr-1" />
              Back to Home
            </Link>
            {user ? (
              <Link to="/dashboard">
                <Button size="sm" variant="outline">Dashboard</Button>
              </Link>
            ) : (
              <Button size="sm" onClick={handleLogin}>Login</Button>
            )}
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-12 space-y-8">
        {/* Title */}
        <div className="text-center">
          <h1 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-2">Choose Your Plan</h1>
          <p className="text-neutral-600">
            Simple pricing for every business. Start free, upgrade anytime.
          </p>
        </div>

        {/* Benefits */}
        <div className="flex justify-center gap-8 flex-wrap">
          <div className="flex items-center gap-2 text-sm text-neutral-600">
            <Shield className="w-4 h-4 text-green-600" />
            Secure Payments
          </div>
          <div className="flex items-center gap-2 text-sm text-neutral-600">
            <Zap className="w-4 h-4 text-yellow-600" />
            Instant Activation
          </div>
          <div className="flex items-center gap-2 text-sm text-neutral-600">
            <CreditCard className="w-4 h-4 text-indigo-600" />
            Cancel Anytime
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-6">
          {PLANS.map((plan) => (
            <Card 
              key={plan.id}
              className={`relative border-2 ${plan.popular ? 'border-indigo-600 shadow-xl' : 'border-neutral-200'} bg-white`}
              data-testid={`plan-card-${plan.id}`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge className="bg-indigo-600 text-white">
                    <Sparkles className="w-3 h-3 mr-1" />
                    Most Popular
                  </Badge>
                </div>
              )}
              
              <CardHeader className="text-center pb-0">
                <CardTitle className="text-xl">{plan.name}</CardTitle>
                <div className="mt-4">
                  <span className="text-4xl font-bold">₹{plan.price.toLocaleString()}</span>
                  <span className="text-neutral-500">{plan.period}</span>
                </div>
              </CardHeader>
              
              <CardContent className="pt-6">
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <Check className={`w-5 h-5 ${plan.popular ? 'text-indigo-600' : 'text-green-600'}`} />
                      <span className="text-neutral-700">{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <Button
                  onClick={() => handlePayment(plan.id)}
                  disabled={loading !== null}
                  className={`w-full ${plan.popular ? 'bg-indigo-600 hover:bg-indigo-700' : ''}`}
                  variant={plan.popular ? 'default' : 'outline'}
                  data-testid={`subscribe-${plan.id}`}
                >
                  {loading === plan.id ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-4 h-4 mr-2" />
                      Subscribe Now
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* FAQ / Info */}
        <Card className="bg-neutral-50 border-0">
          <CardContent className="pt-6">
            <div className="grid md:grid-cols-3 gap-6 text-center">
              <div>
                <h3 className="font-semibold mb-2">What's a SKU Credit?</h3>
                <p className="text-sm text-neutral-600">
                  1 SKU Credit = 1 product with up to 4 AI-generated images. 
                  Videos cost an extra 0.5 credit per SKU.
                </p>
              </div>
              <div>
                <h3 className="font-semibold mb-2">How do renewals work?</h3>
                <p className="text-sm text-neutral-600">
                  Plans renew monthly. You'll receive a reminder before your plan expires. 
                  Unused credits don't roll over.
                </p>
              </div>
              <div>
                <h3 className="font-semibold mb-2">Need a custom plan?</h3>
                <p className="text-sm text-neutral-600">
                  For enterprise requirements or custom quotas, 
                  contact us at Info@jinistudio.com
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
